import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { observer } from 'mobx-react-lite';
import {
  TbPlus,
  TbLayoutSidebar,
  TbLayoutSidebarLeftCollapse,
  TbX,
  TbArrowUp,
  TbArrowsMaximize,
  TbArrowsMinimize,
  TbTrash,
  TbMicrophone,
  TbPlayerStop,
  TbAdjustmentsHorizontal,
  TbUpload,
  TbFileText,
  TbCircleCheck,
  TbFileTypePdf,
  TbFileTypeDoc,
  TbPaperclip
} from 'react-icons/tb';
import agentStore from '../../store/agent';
import useAgentPanel from './useAgentPanel';
import {
  transcribeAudio,
  uploadKnowledge,
  listKnowledge,
  deleteKnowledge,
  useGetAgentInfo
} from '../../services/agent.service';
import {
  PanelRoot,
  ResizeHandle,
  SessionSidebar,
  SidebarInner,
  SidebarHeader,
  SidebarTitle,
  NewChatBtn,
  SearchBox,
  SessionList,
  SessionItem,
  SessionItemRow,
  SessionDeleteBtn,
  SessionDate,
  ChatPane,
  PanelHeader,
  HeaderBrand,
  AgentIconWrapper,
  HeaderTitle,
  IconBtn,
  MessagesArea,
  MessageRow,
  MessageBubble,
  MessageMeta,
  StreamingCursor,
  TypingRow,
  TypingBubble,
  Dot,
  WelcomeWrap,
  WelcomeIcon,
  WelcomeTitle,
  WelcomeSubtitle,
  SuggestionList,
  SuggestionChip,
  InputArea,
  InputRow,
  StyledTextarea,
  SendBtn,
  StopBtn,
  StepsList,
  StepItem,
  StepDot,
  StepText,
  InputHint,
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalCancelBtn,
  ModalDeleteBtn,
  ConfirmButtonRow,
  ConfirmYesBtn,
  ConfirmNoBtn,
  MicBtn,
  SettingsMenuWrapper,
  SettingsDropdown,
  SettingsMenuItem,
  KnowledgeModalCard,
  KnowledgeModalTitle,
  KnowledgeModalSubtitle,
  KnowledgeTitleInput,
  DropZone,
  DropZoneIcon,
  DropZoneText,
  DropZoneHint,
  FileList,
  FileChip,
  KnowledgeDivider,
  KnowledgeTextarea,
  KnowledgeModalActions,
  KnowledgeSubmitBtn,
  KnowledgeSuccessBadge,
  KnowledgeExistingSection,
  KnowledgeExistingHeader,
  KnowledgeExistingList,
  KnowledgeExistingItem,
  KnowledgeExistingInfo,
  KnowledgeExistingName,
  KnowledgeExistingMeta,
  KnowledgeExistingDeleteBtn,
  KnowledgeExistingEmpty,
  InlineActionBtn,
  MarkdownTableWrapper,
  MarkdownTable,
  MarkdownThead,
  MarkdownTh,
  MarkdownTr,
  MarkdownTd,
  PromptsDrawer,
  PromptsDrawerHeader,
  PromptsDrawerItem,
  PopularPromptsSection,
  PopularPromptsLabel,
  PopularPromptsList,
  PopularPromptChip,
  FileChipsRow,
  SessionFileChip,
  PaperclipBtn,
  TextareaWrapper
} from './style';
import { reportError } from '../../services/logger';

// ─── Asterisk icon (reused from ChatWidget assets logic) ──────────────────────
const SUGGESTIONS = ['What can Datox Cowork do for me?'];

const DEFAULT_PROMPTS = [
  'List my projects',
  'Show periods in my project',
  'Show my source funds',
  'Who has been most active this week',
  'Show failed operations from audit logs',
  'Summarise recent team activity',
  'Check approval status for my project',
  'List users in my organisation'
];

const PROMPTS_STORAGE_KEY = 'datox_popular_prompts';
const MAX_POPULAR = 5;

// ─── Popular prompts hook (localStorage-backed frequency tracker) ─────────────
const usePopularPrompts = () => {
  const [popularPrompts, setPopularPrompts] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(PROMPTS_STORAGE_KEY) || '{}');
      const sorted = Object.entries(stored)
        .sort(([, a], [, b]) => b - a)
        .slice(0, MAX_POPULAR)
        .map(([prompt]) => prompt);
      return sorted.length > 0 ? sorted : DEFAULT_PROMPTS.slice(0, MAX_POPULAR);
    } catch {
      return DEFAULT_PROMPTS.slice(0, MAX_POPULAR);
    }
  });

  const recordPrompt = useCallback(text => {
    if (!text?.trim()) return;
    try {
      const stored = JSON.parse(localStorage.getItem(PROMPTS_STORAGE_KEY) || '{}');
      stored[text.trim()] = (stored[text.trim()] || 0) + 1;
      localStorage.setItem(PROMPTS_STORAGE_KEY, JSON.stringify(stored));
      const sorted = Object.entries(stored)
        .sort(([, a], [, b]) => b - a)
        .slice(0, MAX_POPULAR)
        .map(([prompt]) => prompt);
      setPopularPrompts(sorted);
    } catch (_e) {
      // ignore localStorage errors in private browsing or quota exceeded
    }
  }, []);

  return { popularPrompts, recordPrompt };
};

// ─── Add Knowledge modal ──────────────────────────────────────────────────────
const ACCEPTED_TYPES = '.pdf,.docx,.txt';
const ACCEPTED_MIME = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
]);

const FileIcon = ({ name }) => {
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf') return <TbFileTypePdf size={14} />;
  if (ext === 'docx') return <TbFileTypeDoc size={14} />;
  return <TbFileText size={14} />;
};

const firstSentence = text => {
  if (!text) return '';
  const m = text.match(/[^.!?\n]{4,}[.!?]/);
  return m ? m[0].trim() : text.slice(0, 80);
};

const KnowledgeItemMeta = ({ item }) => {
  if (item.source === 'text') {
    return <KnowledgeExistingMeta>{firstSentence(item.first_chunk)}</KnowledgeExistingMeta>;
  }
  return <KnowledgeExistingMeta>{item.source}</KnowledgeExistingMeta>;
};

const AddKnowledgeModal = ({ onClose }) => {
  const [files, setFiles] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [title, setTitle] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successCount, setSuccessCount] = useState(null);
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const fileInputRef = useRef(null);

  const fetchItems = async () => {
    try {
      const res = await listKnowledge();
      setKnowledgeItems(res?.items ?? []);
    } catch {
      setKnowledgeItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const addFiles = newFiles => {
    const valid = Array.from(newFiles).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return ['.pdf', '.docx', '.txt'].includes(ext);
    });
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...valid.filter(f => !existing.has(f.name + f.size))];
    });
  };

  const removeFile = idx => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    if (isSubmitting || (!files.length && !pasteText.trim())) return;
    setIsSubmitting(true);
    try {
      const result = await uploadKnowledge({ files, text: pasteText, title });
      setSuccessCount(result?.chunks_ingested ?? 0);
      setFiles([]);
      setPasteText('');
      setTitle('');
      await fetchItems();
    } catch (err) {
      reportError('AgentPanel.knowledgeUpload', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async item => {
    try {
      await deleteKnowledge({ source: item.source, title: item.title });
      setKnowledgeItems(prev =>
        prev.filter(i => !(i.source === item.source && i.title === item.title))
      );
    } catch (err) {
      reportError('AgentPanel.knowledgeDelete', err);
    }
  };

  const canSubmit = (files.length > 0 || pasteText.trim().length > 0) && !isSubmitting;

  return (
    <ModalOverlay onClick={onClose}>
      <KnowledgeModalCard onClick={e => e.stopPropagation()}>
        <div>
          <KnowledgeModalTitle>Add Knowledge</KnowledgeModalTitle>
          <KnowledgeModalSubtitle style={{ marginTop: 4 }}>
            Upload files or paste text to give the agent personal context. Only you can access this
            knowledge.
          </KnowledgeModalSubtitle>
        </div>

        <KnowledgeTitleInput
          placeholder="Label (optional) — e.g. Q1 2025 Fund Overview"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        {/* ─ File drop zone ─ */}
        <DropZone
          $active={isDragging}
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            style={{ display: 'none' }}
            onChange={e => addFiles(e.target.files)}
          />
          <DropZoneIcon $active={isDragging}>
            <TbUpload size={24} />
          </DropZoneIcon>
          <DropZoneText>
            Drag & drop files here or <strong>browse</strong>
          </DropZoneText>
          <DropZoneHint>PDF, DOCX, TXT &middot; up to 20 MB each</DropZoneHint>
        </DropZone>

        {files.length > 0 && (
          <FileList>
            {files.map((f, i) => (
              <FileChip key={i}>
                <FileIcon name={f.name} />
                <span title={f.name}>{f.name}</span>
                <button onClick={() => removeFile(i)} title="Remove">
                  <TbX size={13} />
                </button>
              </FileChip>
            ))}
          </FileList>
        )}

        <KnowledgeDivider>or paste text</KnowledgeDivider>

        <KnowledgeTextarea
          placeholder="Paste text content here…"
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
        />

        <KnowledgeModalActions>
          <ModalCancelBtn onClick={onClose}>Cancel</ModalCancelBtn>
          {successCount !== null ? (
            <KnowledgeSuccessBadge>
              <TbCircleCheck size={15} />
              Knowledge added
            </KnowledgeSuccessBadge>
          ) : (
            <KnowledgeSubmitBtn onClick={handleSubmit} disabled={!canSubmit}>
              <TbUpload size={14} />
              {isSubmitting ? 'Uploading…' : 'Add to Knowledge'}
            </KnowledgeSubmitBtn>
          )}
        </KnowledgeModalActions>

        <KnowledgeExistingSection>
          <KnowledgeExistingHeader>Your Knowledge</KnowledgeExistingHeader>
          {loadingItems ? (
            <KnowledgeExistingEmpty>Loading…</KnowledgeExistingEmpty>
          ) : knowledgeItems.length === 0 ? (
            <KnowledgeExistingEmpty>No knowledge added yet.</KnowledgeExistingEmpty>
          ) : (
            <KnowledgeExistingList>
              {knowledgeItems.map((item, i) => (
                <KnowledgeExistingItem key={i}>
                  <FileIcon name={item.source} />
                  <KnowledgeExistingInfo>
                    <KnowledgeExistingName>{item.title}</KnowledgeExistingName>
                    <KnowledgeItemMeta item={item} />
                  </KnowledgeExistingInfo>
                  <KnowledgeExistingDeleteBtn onClick={() => handleDeleteItem(item)} title="Remove">
                    <TbTrash size={14} />
                  </KnowledgeExistingDeleteBtn>
                </KnowledgeExistingItem>
              ))}
            </KnowledgeExistingList>
          )}
        </KnowledgeExistingSection>
      </KnowledgeModalCard>
    </ModalOverlay>
  );
};

// ─── Session sidebar ──────────────────────────────────────────────────────────
const SessionsSidebar = ({
  chats,
  activeChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  isOpen,
  onToggle,
  searchQuery,
  onSearchChange
}) => {
  const sorted = [...chats].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const formatDate = dateStr => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <SessionSidebar $open={isOpen}>
      <SidebarInner>
        <SidebarHeader>
          <IconBtn onClick={onToggle} title="Collapse sessions">
            <TbLayoutSidebarLeftCollapse size={15} />
          </IconBtn>
          <SidebarTitle>Sessions</SidebarTitle>
        </SidebarHeader>
        <NewChatBtn onClick={onNewChat}>
          <TbPlus size={13} />
          New chat
        </NewChatBtn>
        <SearchBox
          placeholder="Search…"
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
        />
        <SessionList>
          {sorted.map(chat => (
            <SessionItemRow key={chat.id}>
              <SessionItem
                $active={chat.id === activeChatId}
                onClick={() => onSelectChat(chat.id)}
                title={chat.title}
                style={{ flex: 1, paddingRight: '28px' }}
              >
                {chat.title || 'Untitled'}
                <SessionDate>{formatDate(chat.created_at)}</SessionDate>
              </SessionItem>
              <SessionDeleteBtn
                title="Delete conversation"
                onClick={e => {
                  e.stopPropagation();
                  onDeleteChat(chat);
                }}
              >
                <TbTrash size={13} />
              </SessionDeleteBtn>
            </SessionItemRow>
          ))}
        </SessionList>
      </SidebarInner>
    </SessionSidebar>
  );
};

// ─── Confirmation modal ───────────────────────────────────────────────────────
const DeleteConfirmModal = ({ chat, onCancel, onConfirm }) => (
  <ModalOverlay onClick={onCancel}>
    <ModalCard onClick={e => e.stopPropagation()}>
      <ModalTitle>Delete conversation?</ModalTitle>
      <ModalBody>
        &quot;{chat.title || 'Untitled'}&quot; will be permanently deleted and cannot be recovered.
      </ModalBody>
      <ModalActions>
        <ModalCancelBtn onClick={onCancel}>Cancel</ModalCancelBtn>
        <ModalDeleteBtn onClick={onConfirm}>Delete</ModalDeleteBtn>
      </ModalActions>
    </ModalCard>
  </ModalOverlay>
);

// ─── Typing indicator ─────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <TypingRow>
    <TypingBubble>
      {[0, 1, 2].map(i => (
        <Dot key={i} $i={i} />
      ))}
    </TypingBubble>
  </TypingRow>
);

// ─── Message list ────────────────────────────────────────────────────────────
const MessageList = ({
  messages,
  isStreaming,
  streamingMessageId,
  isLoadingMessages,
  onSelectSuggestion,
  onConfirmReply,
  popularPrompts
}) => {
  const bottomRef = useRef(null);

  const preprocessMarkdown = content =>
    content?.replace(
      /\[([^\]]+)\]\(datox-action:([^)]+)\)/g,
      (_, text, prompt) => `[${text}](https://datox-action/${encodeURIComponent(prompt)})`
    ) ?? content;

  const markdownComponents = {
    a: ({ href, children }) => {
      if (href?.startsWith('https://datox-action/')) {
        const prompt = decodeURIComponent(href.replace('https://datox-action/', ''));
        return (
          <InlineActionBtn onClick={() => onSelectSuggestion(prompt)}>{children}</InlineActionBtn>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    li: ({ children }) => {
      const kids = React.Children.toArray(children);
      const isActionOnly =
        kids.length === 1 &&
        React.isValidElement(kids[0]) &&
        kids[0].props?.href?.startsWith?.('https://datox-action/');
      if (isActionOnly) return <li style={{ listStyle: 'none', margin: '2px 0' }}>{children}</li>;
      return <li>{children}</li>;
    },
    table: ({ node, ...props }) => (
      <MarkdownTableWrapper>
        <MarkdownTable {...props} />
      </MarkdownTableWrapper>
    ),
    thead: ({ node, ...props }) => <MarkdownThead {...props} />,
    tbody: ({ node, ...props }) => <tbody {...props} />,
    tr: ({ node, ...props }) => <MarkdownTr {...props} />,
    th: ({ node, ...props }) => <MarkdownTh {...props} />,
    td: ({ node, ...props }) => <MarkdownTd {...props} />
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  if (!messages.length && !isLoadingMessages) {
    return (
      <MessagesArea>
        <WelcomeWrap>
          <WelcomeIcon>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
              <path d="M19 2 L19.7 4.3 L22 5 L19.7 5.7 L19 8 L18.3 5.7 L16 5 L18.3 4.3 Z" />
              <path d="M5 16 L5.5 17.5 L7 18 L5.5 18.5 L5 20 L4.5 18.5 L3 18 L4.5 17.5 Z" />
            </svg>
          </WelcomeIcon>
          <WelcomeTitle>Datox Cowork</WelcomeTitle>
          <WelcomeSubtitle>
            Datox Cowork can help you answer questions, complete tasks, discover and automate your
            work.
          </WelcomeSubtitle>
          <SuggestionList>
            {SUGGESTIONS.map(s => (
              <SuggestionChip key={s} onClick={() => onSelectSuggestion(s)}>
                {s}
              </SuggestionChip>
            ))}
          </SuggestionList>
          {popularPrompts?.length > 0 && (
            <PopularPromptsSection>
              <PopularPromptsLabel>Popular prompts</PopularPromptsLabel>
              <PopularPromptsList>
                {popularPrompts.map(p => (
                  <PopularPromptChip key={p} onClick={() => onSelectSuggestion(p)}>
                    {p}
                  </PopularPromptChip>
                ))}
              </PopularPromptsList>
            </PopularPromptsSection>
          )}
        </WelcomeWrap>
      </MessagesArea>
    );
  }

  return (
    <MessagesArea>
      {messages.map(msg => {
        const isUser = msg.author_type === 'User';
        const isCurrentStreaming = msg.id === streamingMessageId;
        return (
          <MessageRow key={msg.id} $isUser={isUser}>
            <MessageBubble $isUser={isUser}>
              {isUser ? (
                msg.content
              ) : (
                <>
                  {msg.steps?.length > 0 && (
                    <StepsList>
                      {msg.steps.map((step, i) => (
                        <StepItem key={i}>
                          <StepDot $done={step.done} />
                          <StepText $done={step.done}>{step.message}</StepText>
                        </StepItem>
                      ))}
                    </StepsList>
                  )}
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {preprocessMarkdown(msg.content)}
                  </ReactMarkdown>
                  {isCurrentStreaming && <StreamingCursor />}
                  {!isCurrentStreaming && msg.isInterrupted && (
                    <ConfirmButtonRow>
                      <ConfirmYesBtn onClick={() => onConfirmReply(true)}>Yes</ConfirmYesBtn>
                      <ConfirmNoBtn onClick={() => onConfirmReply(false)}>No</ConfirmNoBtn>
                    </ConfirmButtonRow>
                  )}
                </>
              )}
            </MessageBubble>
            {!isCurrentStreaming && (
              <MessageMeta>
                {msg.created_at
                  ? new Date(msg.created_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : ''}
              </MessageMeta>
            )}
          </MessageRow>
        );
      })}
      {isStreaming && !streamingMessageId && <TypingIndicator />}
      <div ref={bottomRef} />
    </MessagesArea>
  );
};

// ─── Message input ────────────────────────────────────────────────────────────
const MessageInput = ({
  value,
  onChange,
  onSend,
  onStop,
  isStreaming,
  llmModel,
  sessionFiles,
  onAttachFile,
  onRemoveFile
}) => {
  const ref = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleInput = e => {
    const ta = ref.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    onChange(e.target.value);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setIsTranscribing(true);
        try {
          const text = await transcribeAudio(blob);
          if (text) {
            onSend(text.trim());
          }
        } catch (err) {
          reportError('AgentPanel.transcription', err);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      reportError('AgentPanel.microphone', err);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  return (
    <InputArea>
      {sessionFiles && sessionFiles.length > 0 && (
        <FileChipsRow>
          {sessionFiles.map(f => (
            <SessionFileChip key={f.filename}>
              <TbFileText size={13} />
              <span title={f.filename}>{f.filename}</span>
              <button type="button" onClick={() => onRemoveFile?.(f.filename)} title="Remove file">
                <TbX size={12} />
              </button>
            </SessionFileChip>
          ))}
        </FileChipsRow>
      )}
      <InputRow>
        <TextareaWrapper>
          <PaperclipBtn
            type="button"
            title="Attach file (PDF, DOCX, TXT)"
            disabled={isStreaming}
            onClick={() => fileInputRef.current?.click()}
          >
            <TbPaperclip size={16} />
          </PaperclipBtn>
          <StyledTextarea
            ref={ref}
            value={value}
            placeholder={isTranscribing ? 'Transcribing…' : 'I want to…'}
            onChange={handleInput}
            onKeyDown={handleKey}
            disabled={isStreaming || isTranscribing}
            rows={1}
          />
        </TextareaWrapper>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt"
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) onAttachFile?.(file);
            e.target.value = '';
          }}
        />
        <MicBtn
          type="button"
          onClick={toggleRecording}
          $recording={isRecording}
          disabled={isStreaming || isTranscribing}
          title={isRecording ? 'Stop recording' : 'Record voice message'}
        >
          {isRecording ? <TbPlayerStop size={16} /> : <TbMicrophone size={16} />}
        </MicBtn>
        {isStreaming ? (
          <StopBtn type="button" onClick={onStop} title="Stop">
            <TbPlayerStop size={16} />
          </StopBtn>
        ) : (
          <SendBtn onClick={() => onSend()} disabled={!value.trim() || isStreaming}>
            <TbArrowUp size={16} />
          </SendBtn>
        )}
      </InputRow>
      <InputHint>
        <span style={{ marginLeft: 'auto' }}>
          {isRecording
            ? 'Recording… click mic to stop'
            : isTranscribing
              ? 'Transcribing audio…'
              : 'Enter to send · Shift+Enter for newline'}
        </span>
      </InputHint>
    </InputArea>
  );
};

// ─── Main panel ───────────────────────────────────────────────────────────────
const MIN_WIDTH = 320;
const DEFAULT_WIDTH = 580;

const AgentPanel = () => {
  const isOpen = agentStore.isAgentPanelOpen;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // chat object pending deletion
  const [isKnowledgeModalOpen, setIsKnowledgeModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startWidth: DEFAULT_WIDTH });

  // Close settings dropdown on outside click
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = e => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [settingsOpen]);

  const handleResizeMouseDown = useCallback(
    e => {
      if (isFullscreen) return;
      e.preventDefault();
      dragRef.current = { active: true, startX: e.clientX, startWidth: panelWidth };
    },
    [isFullscreen, panelWidth]
  );

  useEffect(() => {
    const onMouseMove = e => {
      if (!dragRef.current.active) return;
      const delta = dragRef.current.startX - e.clientX;
      const next = Math.min(
        Math.max(dragRef.current.startWidth + delta, MIN_WIDTH),
        window.innerWidth - 60
      );
      setPanelWidth(next);
    };
    const onMouseUp = () => {
      dragRef.current.active = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const { data: agentInfo } = useGetAgentInfo();
  const llmModel = agentInfo?.llm_model;

  const {
    messages,
    inputValue,
    activeChatId,
    chats,
    searchQuery,
    isLoadingMessages,
    isStreaming,
    streamingMessageId,
    setInputValue,
    onSendMessage,
    injectLocalMessages,
    onStop,
    onConfirmInterrupt,
    onNewChat,
    onSelectChat,
    onDeleteChat,
    onSearchChange,
    sessionFiles,
    attachSessionFile,
    removeSessionFile
  } = useAgentPanel();

  const { popularPrompts, recordPrompt } = usePopularPrompts();

  const handleSend = useCallback(
    text => {
      const msg = text ?? inputValue;
      if (msg?.trim()) recordPrompt(msg.trim());
      onSendMessage(msg);
    },
    [inputValue, onSendMessage, recordPrompt]
  );

  if (!isOpen) return null;

  const handleDeleteRequest = chat => setConfirmDelete(chat);
  const handleDeleteCancel = () => setConfirmDelete(null);
  const handleDeleteConfirm = () => {
    if (confirmDelete) onDeleteChat(confirmDelete.id);
    setConfirmDelete(null);
  };

  return (
    <PanelRoot $open={isOpen} $width={panelWidth} $fullscreen={isFullscreen}>
      {/* Confirmation modal */}
      {confirmDelete && (
        <DeleteConfirmModal
          chat={confirmDelete}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Add Knowledge modal */}
      {isKnowledgeModalOpen && <AddKnowledgeModal onClose={() => setIsKnowledgeModalOpen(false)} />}

      {/* Left resize handle */}
      {!isFullscreen && <ResizeHandle onMouseDown={handleResizeMouseDown} />}

      {/* Sessions sidebar */}
      <SessionsSidebar
        chats={chats}
        activeChatId={activeChatId}
        onSelectChat={onSelectChat}
        onNewChat={onNewChat}
        onDeleteChat={handleDeleteRequest}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(p => !p)}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
      />

      {/* Main chat pane */}
      <ChatPane>
        <PanelHeader>
          <HeaderBrand>
            {!sidebarOpen && (
              <>
                <IconBtn onClick={() => setSidebarOpen(true)} title="Open sessions">
                  <TbLayoutSidebar size={15} />
                </IconBtn>
                <IconBtn onClick={onNewChat} title="New chat">
                  <TbPlus size={15} />
                </IconBtn>
              </>
            )}
            <AgentIconWrapper>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
                <path d="M19 2 L19.7 4.3 L22 5 L19.7 5.7 L19 8 L18.3 5.7 L16 5 L18.3 4.3 Z" />
                <path d="M5 16 L5.5 17.5 L7 18 L5.5 18.5 L5 20 L4.5 18.5 L3 18 L4.5 17.5 Z" />
              </svg>
            </AgentIconWrapper>
            <div>
              <HeaderTitle>Datox Cowork</HeaderTitle>
            </div>
          </HeaderBrand>
          <SettingsMenuWrapper ref={settingsRef}>
            {settingsOpen && (
              <SettingsDropdown>
                <SettingsMenuItem
                  onClick={() => {
                    setSettingsOpen(false);
                    setIsKnowledgeModalOpen(true);
                  }}
                >
                  <TbFileText size={14} />
                  Add Knowledge
                </SettingsMenuItem>
              </SettingsDropdown>
            )}
            <IconBtn
              onClick={() => setSettingsOpen(p => !p)}
              title="Settings"
              style={settingsOpen ? { background: '#f1f3f5', color: '#374151' } : {}}
            >
              <TbAdjustmentsHorizontal size={15} />
            </IconBtn>
          </SettingsMenuWrapper>
          <IconBtn
            onClick={() => setIsFullscreen(f => !f)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <TbArrowsMinimize size={15} /> : <TbArrowsMaximize size={15} />}
          </IconBtn>
          <IconBtn onClick={() => agentStore.toggleAgentPanel()} title="Close agent panel">
            <TbX size={15} />
          </IconBtn>
        </PanelHeader>

        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          streamingMessageId={streamingMessageId}
          isLoadingMessages={isLoadingMessages}
          onSelectSuggestion={handleSend}
          onConfirmReply={approved => onConfirmInterrupt(approved)}
          popularPrompts={popularPrompts}
        />

        <MessageInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          onStop={onStop}
          isStreaming={isStreaming}
          popularPrompts={popularPrompts}
          sessionFiles={sessionFiles}
          onAttachFile={attachSessionFile}
          onRemoveFile={removeSessionFile}
        />
      </ChatPane>
    </PanelRoot>
  );
};

export default observer(AgentPanel);
